/**
 * WG-3 citation-token rendering (Browser QA defect, 21-10).
 *
 * The backend evidence contract instructs the LLM to cite sources by their
 * backend provenance id: ``[src_xxxxxxxx]``. Those ids are LLM-facing and
 * must NOT be shown to the user verbatim (they look like broken internal
 * links). The user-facing rendering maps each token to a numbered marker
 * ``[1]``, ``[2]``… aligned with the order of the ``sources`` block the
 * backend already renders above the answer. Unknown/unresolvable ids
 * (e.g. history loaded before sources were attached) are stripped.
 *
 * Pure function — no DOM, unit-testable.
 */

export interface CitationSourceLike {
  sourceId?: string;
}

const TOKEN_RE = /\[src_[0-9a-f]{6,32}\]/g;

/**
 * Replace ``[src_id]`` tokens with ``[n]`` markers aligned to the sources
 * list order. Tokens whose id is not in ``sources`` are removed.
 * Returns the content unchanged when there are no tokens.
 */
export function mapCitationTokens(
  content: string,
  sources: CitationSourceLike[] | undefined | null,
): string {
  if (!content || !content.includes("[src_")) return content;
  const indexById = new Map<string, number>();
  for (const s of sources ?? []) {
    if (s?.sourceId && !indexById.has(s.sourceId)) {
      indexById.set(s.sourceId, indexById.size + 1);
    }
  }
  return content.replace(TOKEN_RE, (token) => {
    const n = indexById.get(token.slice(1, -1));
    return n ? `[${n}]` : "";
  });
}

/**
 * Sources UX (21-10): same mapping, but markers become Markdown anchors
 * ``[n](#cite-n)`` so the renderer can turn them into clickable citation
 * links that open the Sources panel on the matching row. Raw ``[src_]``
 * ids are still fully stripped — nothing internal reaches the DOM.
 */
export function mapCitationTokensAsLinks(
  content: string,
  sources: CitationSourceLike[] | undefined | null,
): string {
  if (!content || !content.includes("[src_")) return content;
  const indexById = new Map<string, number>();
  for (const s of sources ?? []) {
    if (s?.sourceId && !indexById.has(s.sourceId)) {
      indexById.set(s.sourceId, indexById.size + 1);
    }
  }
  return content.replace(TOKEN_RE, (token) => {
    const n = indexById.get(token.slice(1, -1));
    return n ? `[${n}](#cite-${n})` : "";
  });
}
